// T-17 (T17-D13) — deep links survive sign-in.
//
// The MSAL `state` carries only an unguessable NONCE (`rt1.<nonce>`); the
// return path itself is parked in sessionStorage under that nonce and never
// travels through the identity provider. Every value that can become a
// navigation target passes through `sanitizeReturnTo` — the ONE sanitiser —
// including a value read back from storage.
//
// Kept out of msalConfig.ts on purpose (the web test setup mocks that module).

const KEY_PREFIX = 'autoectd.returnTo.';
const POST_LOGIN_KEY = 'autoectd.postLoginReturnTo';
const STATE_PREFIX = 'rt1.';
const NONCE_RE = /^[A-Za-z0-9_-]{16,64}$/;
const MAX_LENGTH = 2048;
const TTL_MS = 10 * 60 * 1000;

function storage(): typeof window.sessionStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Accept only a same-origin, absolute-path return target. Anything else
 * (other origins, scheme URLs, protocol-relative, backslashes, control chars,
 * over-long, non-strings, the bare root) is null — callers fall back to /home.
 */
export function sanitizeReturnTo(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length > MAX_LENGTH) return null;
  if (raw === '/' || raw[0] !== '/') return null;
  if (raw[1] === '/' || raw[1] === '\\') return null;
  for (let i = 0; i < raw.length; i += 1) {
    const c = raw.charCodeAt(i);
    if (c <= 0x20 || c === 0x7f || c === 0x5c || c === 0x2028 || c === 0x2029) return null;
  }
  try {
    const base = 'http://x.invalid';
    const u = new URL(raw, base);
    if (u.origin !== base) return null;
    if (u.pathname + u.search + u.hash !== raw) return null;
  } catch {
    return null;
  }
  return raw;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Park a valid return path under a fresh nonce. Null when unsuitable / no storage. */
export function stashReturnTo(path: string): string | null {
  const clean = sanitizeReturnTo(path);
  if (clean === null) return null;
  const store = storage();
  if (!store) return null;
  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const nonce = base64Url(bytes);
    store.setItem(KEY_PREFIX + nonce, JSON.stringify({ p: clean, t: Date.now() }));
    return nonce;
  } catch {
    return null;
  }
}

export function encodeReturnState(nonce: string): string {
  return STATE_PREFIX + nonce;
}

/** The nonce out of an MSAL `state`, or null for anything that is not ours. */
export function decodeReturnState(state: unknown): string | null {
  if (typeof state !== 'string' || !state.startsWith(STATE_PREFIX)) return null;
  const nonce = state.slice(STATE_PREFIX.length);
  return NONCE_RE.test(nonce) ? nonce : null;
}

/** Read-once: returns the stashed path (re-sanitised, TTL-checked) and deletes the key. */
export function consumeReturnTo(nonce: string): string | null {
  if (!NONCE_RE.test(nonce)) return null;
  const store = storage();
  if (!store) return null;
  const key = KEY_PREFIX + nonce;
  try {
    const raw = store.getItem(key);
    if (raw === null) return null;
    store.removeItem(key);
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { p, t } = parsed as { p?: unknown; t?: unknown };
    if (typeof t !== 'number' || Date.now() - t > TTL_MS) return null;
    return sanitizeReturnTo(p);
  } catch {
    return null;
  }
}

/**
 * Call once after MSAL init: finishes the redirect handshake, and when the
 * returning `state` is ours, moves the path from its nonce slot to the
 * post-login slot that LoginPage consumes.
 */
export async function capturePostLoginReturnTo(instance: {
  handleRedirectPromise: () => Promise<{ state?: string } | null>;
}): Promise<void> {
  const result = await instance.handleRedirectPromise();
  const nonce = decodeReturnState(result?.state);
  if (nonce === null) return;
  const path = consumeReturnTo(nonce);
  if (path === null) return;
  try {
    storage()?.setItem(POST_LOGIN_KEY, path);
  } catch {
    /* storage unavailable: LoginPage falls back to router state / /home */
  }
}

/** One-time read of the post-login path (re-sanitised); deletes the key. */
export function consumePostLoginReturnTo(): string | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(POST_LOGIN_KEY);
    if (raw === null) return null;
    store.removeItem(POST_LOGIN_KEY);
    return sanitizeReturnTo(raw);
  } catch {
    return null;
  }
}
